// Reads the persisted session, tolerating a malformed value. localStorage is
// user-editable, so a corrupt entry must degrade to "no session" — the route
// guards then treat the visitor as logged out — rather than throw. The throw
// is app-wide, not local: App.js's catch-all route parses this value in an
// inline element expression that is evaluated on every render of the route
// table, so one bad value would white-screen every URL.
// (SEC-071 / #15 tracks the real fix: one central place that reads identity.)
export function readCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('currentUser'));
  } catch (_) {
    return null;
  }
}

// Merges a fetched profile (`GET /users/:userId`) into the cached user.
//
// The cache is read *here*, at write time, so callers must call this after their
// `await getProfile(...)` rather than merging into a copy they took before it:
// the silent token refresh (sessionRefresh.js) rewrites `currentUser.token`
// while the request is in flight, and writing back a stale copy would revert it
// and make the next call 401.
//
// Merged, not assigned: the profile is projected to the public allowlist, a
// subset of what the cache holds. Returns whether the cache was written (false
// when there is no usable session).
//
// Pass the user id the profile was requested for. If a different account signed
// in on this tab while the request was in flight, the cache now belongs to that
// account and the merge is skipped rather than writing the first account's
// identity and plan into it. A profile carrying a different `_id` is refused
// for the same reason.
export function mergeProfileIntoCachedUser(profileUser, requestedUserId) {
  const latest = readCurrentUser();
  if (!latest?.user) return false;
  if (requestedUserId !== undefined && latest.user._id !== requestedUserId) return false;
  if (profileUser?._id && profileUser._id !== latest.user._id) return false;
  Object.assign(latest.user, profileUser);
  localStorage.setItem('currentUser', JSON.stringify(latest));
  return true;
}

// Drops every trace of the session from this browser.
//
// Called on the way out of logout **unconditionally** — including when the
// server rejected the revoke. Those two failures are not symmetric: a refresh
// token left live server-side is a token nobody holds a copy of any more, while
// a session left in localStorage is one the person at the keyboard is still
// signed in with. Refusing to clear because the request failed is how logout
// came to be unusable in the first place (#72).
//
// The 15-minute access TTL makes the failing case ordinary rather than
// exceptional: a tab left open past expiry has a token `verifyToken` will
// refuse, so /users/logout answers 401 and the user must still get logged out.
export function clearStoredSession() {
  try {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('resetPasswordEmail');
    // In-flight OTP flow state, if the user logged out mid-verification.
    sessionStorage.removeItem('verifyCodeFrom');
    sessionStorage.removeItem('otpChallengeToken');
    sessionStorage.removeItem('otpChallengePurpose');
    sessionStorage.removeItem('otpResetToken');
  } catch (_) {
    // Nothing recoverable: a store we cannot write is one the next read will
    // fail on too, and readCurrentUser degrades a bad value to "no session".
  }
}
